import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

function CartPage() {
  const { cartItems, updateQuantity, removeFromCart, getCartTotal, clearCart, addToCart } = useCart();

  // Automatically add Kasango AI Pro when cart page loads
  useEffect(() => {
    const kasangoProduct = {
      id: 'kasango-pro',
      name: 'Kasango AI Pro',
      description: 'Advanced AI-powered forex trading robot',
      price: 1450,
      whopLink: 'https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true'
    };

    // Check if Kasango AI Pro is already in cart
    const hasKasango = cartItems.some(item => item.id === 'kasango-pro');
    
    // If not in cart, add it automatically
    if (!hasKasango) {
      addToCart(kasangoProduct);
    }
  }, [cartItems, addToCart]);

  const handleCheckout = () => {
    // If cart has Kasango AI Pro, redirect to Kasango Whop
    const hasKasango = cartItems.some(item => item.id === 'kasango-pro');
    const hasCashbet = cartItems.some(item => item.id === 'cashbet-ai');

    if (hasKasango && hasCashbet) {
      // Both products - open both in new tabs
      window.open('https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true', '_blank');
      window.open('https://whop.com/cashbet-ai/', '_blank');
    } else if (hasKasango) {
      window.open('https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true', '_blank');
    } else if (hasCashbet) {
      window.open('https://whop.com/cashbet-ai/', '_blank');
    }

    // Clear cart after checkout
    setTimeout(() => {
      clearCart();
    }, 1000);
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-lg border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
                  alt="John Ghatti"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-xl font-light tracking-wide">JOHN GHATTI</span>
            </Link>
          </div>
        </header>

        {/* Loading Cart */}
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-8"></div>
            <h1 className="text-4xl font-light mb-4">Preparing Your Cart...</h1>
            <p className="text-gray-400 font-light mb-8">Adding Kasango AI Pro to your cart</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20">
              <img
                src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
                alt="John Ghatti"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-xl font-light tracking-wide">JOHN GHATTI</span>
          </Link>
        </div>
      </header>

      {/* Cart Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl font-light mb-12">Shopping Cart</h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-zinc-950 border border-zinc-800 p-6 flex gap-6"
                >
                  {/* Product Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-light mb-1">{item.name}</h3>
                        <p className="text-gray-400 text-sm font-light">{item.description}</p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-gray-500 hover:text-white transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-4 mt-4">
                      <span className="text-sm text-gray-400">Quantity:</span>
                      <div className="flex items-center gap-2 border border-zinc-700">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="px-3 py-1 hover:bg-zinc-800 transition-colors"
                        >
                          -
                        </button>
                        <span className="px-4">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="px-3 py-1 hover:bg-zinc-800 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-right">
                    <div className="text-2xl font-light">${item.price}</div>
                    {item.quantity > 1 && (
                      <div className="text-sm text-gray-400 mt-1">
                        ${item.price * item.quantity} total
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-zinc-950 border border-zinc-800 p-6 sticky top-24">
                <h2 className="text-2xl font-light mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal</span>
                    <span>${getCartTotal()}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Shipping</span>
                    <span className="text-green-500">Free</span>
                  </div>
                  <div className="border-t border-zinc-700 pt-4">
                    <div className="flex justify-between text-xl font-light">
                      <span>Total</span>
                      <span>${getCartTotal()}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full py-4 bg-white text-black text-sm font-bold tracking-widest hover:bg-gray-200 transition-colors mb-4"
                >
                  PROCEED TO CHECKOUT
                </button>

                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to cancel your order and clear the cart?')) {
                      clearCart();
                      window.location.href = '/';
                    }
                  }}
                  className="w-full py-3 border border-red-600 text-red-500 text-center text-sm font-light hover:bg-red-950/30 transition-colors mb-4"
                >
                  Cancel Order
                </button>

                <Link
                  to="/"
                  className="block w-full py-3 border border-zinc-700 text-center text-sm font-light hover:bg-zinc-900 transition-colors"
                >
                  Continue Shopping
                </Link>

                {/* Security Badge */}
                <div className="mt-6 flex items-center gap-2 text-xs text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>Secure checkout via Whop</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CartPage;
